import { FormikErrors, FormikTouched } from "formik";
import React, { useCallback, useState } from "react";
import { Button, Form, Modal } from "react-bootstrap";
import { Checkbox, Icon } from "../../components";
import { useI18n } from "../../lib/i18n/i18n_provider";
import { Spacer } from "../../offering/spacer";

import "./payment_cta.css";
import { MarketingConsent, MarketingConsentInput } from "./marketing_consent";

interface PaymentCTAProps {
  termsURL?: string;
  partnerName: string;
  onSubmit: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  disabled: boolean;
  isPaymentRequired?: boolean;
  isContinueToPayment: boolean;
  membership?: boolean;
  onTermsClick: () => void;
  membershipPaymentCTALabel?: string;
}

export function PaymentCTA(props: PaymentCTAProps) {
  const {
    onSubmit,
    disabled,
    termsURL,
    partnerName,
    isPaymentRequired = true,
    isContinueToPayment,
    membership,
    onTermsClick,
    membershipPaymentCTALabel,
  } = props;
  const [open, setOpen] = useState(false);
  const { fbt } = useI18n();
  return (
    <div className="payment-cta">
      <Spacer size={29} />
      {termsURL && (
        <>
          <p className="payment-cta__term">
            {fbt(
              `By providing your card information, you allow ${fbt.param(
                "partnerName",
                partnerName
              )} to charge your card for future payments in accordance with their `
            )}
            <Button
              variant="link"
              className="p-0"
              onClick={() => {
                setOpen(true);
                onTermsClick();
              }}
            >
              {fbt("Terms of Service")}
            </Button>
            {"."}
          </p>
          <Modal
            size="xl"
            className="terms_and_conditions"
            show={open}
            onHide={() => setOpen(false)}
          >
            <Modal.Header closeButton></Modal.Header>
            <Modal.Body>
              <iframe
                className="offering-checkout-iframe"
                title="offering-checkout-iframe"
                src={termsURL}
              />
            </Modal.Body>
          </Modal>
        </>
      )}
      <div className="row">
        <div className="col">
          <Button
            variant="primary"
            size="lg"
            disabled={disabled}
            onClick={onSubmit}
            className="payment-cta__confirm"
          >
            {membership ? (
              <h5 className="m-0 ps-2 pe-2 pt-1">
                {fbt(membershipPaymentCTALabel || "Confirm and subscribe")}
              </h5>
            ) : isContinueToPayment && isPaymentRequired ? (
              <h5 className="m-0 ps-2 pe-2 pt-1">
                {fbt("Continue with Payment")}
              </h5>
            ) : (
              <>
                <Icon name="lock-alt" />
                <h5 className="m-0 ps-2 pe-2 pt-1">
                  {isPaymentRequired ? fbt("Confirm and Pay") : fbt("Confirm")}
                </h5>
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}

interface TermsInput {
  termsChecked?: boolean;
}

interface PaymentCTAWithTermsProps {
  terms: string;
  onSubmit: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  disabled: boolean;
  isPaymentRequired: boolean;
  isContinueToPayment: boolean;
  values: TermsInput;
  errors: FormikErrors<TermsInput>;
  setFieldValue: (field: string, value: any) => void;
  touched: FormikTouched<TermsInput>;
  onTermsClick: () => void;
  setFieldTouched?: (value: string) => void;
  onErrorTextRef?: (ref: HTMLElement | null, errorId: keyof TermsInput) => void;
  onInputBorderRef?: (
    ref: HTMLElement | null,
    errorId: keyof TermsInput
  ) => void;
}

export function PaymentCTAWithTerms(props: PaymentCTAWithTermsProps) {
  const {
    values,
    errors,
    setFieldValue,
    onSubmit,
    disabled,
    terms,
    isPaymentRequired,
    isContinueToPayment,
    touched,
    onTermsClick,
    setFieldTouched,
    onErrorTextRef,
    onInputBorderRef,
  } = props;
  const { fbt } = useI18n();
  const [open, setOpen] = useState(false);
  const [termsUrl, setTermsUrl] = useState<string>();

  /** This function to find the URL in the terms and conditions
   * To handle display this url in an iframe dialog
   */
  const contentClickHandler = useCallback(
    (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
      const targetLink = (e.target as Element).closest("a");
      if (!targetLink) return;
      onTermsClick();
      if (targetLink.target === "_blank") return;

      e.preventDefault();
      setTermsUrl(targetLink.href);
      setOpen(true);
    },
    [onTermsClick]
  );

  return (
    <div className="payment-cta-with-terms">
      <Spacer size={29} />
      <div className="row">
        <div className="col-12 mb-4">
          <div className="payment-cta-with-terms__container">
            <Checkbox
              value={values.termsChecked}
              onChange={(v) => setFieldValue("termsChecked", v)}
              testID={"payment-cta-with-terms_checked-checkbox"}
              isInvalid={!!(errors.termsChecked && touched.termsChecked)}
              onBlur={() => setFieldTouched?.("termsChecked")}
              borderRef={(ref) => onInputBorderRef?.(ref, "termsChecked")}
            />

            <div
              className="payment-cta-with-terms__terms text-break"
              onClick={contentClickHandler}
              dangerouslySetInnerHTML={{ __html: terms }}
              data-testid="payment-cta-with-terms__terms-testId"
            />
          </div>
          {errors.termsChecked && touched.termsChecked && (
            <Form.Text
              className="text-danger"
              ref={(ref: HTMLElement | null) =>
                onErrorTextRef?.(ref, "termsChecked")
              }
            >
              {errors.termsChecked}
            </Form.Text>
          )}
        </div>
        <div className="col-12">
          <Button
            variant="primary"
            size="lg"
            disabled={disabled}
            onClick={onSubmit}
            className="payment-cta__confirm"
            data-testid="payment-cta-with-terms_confirm-button"
          >
            {isContinueToPayment && isPaymentRequired ? (
              <h5 className="m-0">{fbt("Continue with Payment")}</h5>
            ) : (
              <>
                <img
                  src="https://meet-production.s3.us-west-1.amazonaws.com/icons/offerings/lock.svg"
                  alt="payment confirm"
                />
                <h5 className="m-0">
                  {isPaymentRequired ? fbt("Confirm and Pay") : fbt("Confirm")}
                </h5>
              </>
            )}
          </Button>
        </div>
      </div>
      <Modal
        size="xl"
        fullscreen="sm-down"
        className="terms_and_conditions"
        show={open}
        onHide={() => setOpen(false)}
      >
        <Modal.Header closeButton></Modal.Header>
        <Modal.Body>
          <iframe
            className="offering-checkout-iframe"
            title="offering-checkout-iframe"
            src={termsUrl}
          />
        </Modal.Body>
      </Modal>
    </div>
  );
}

interface TermsInput {
  termsChecked?: boolean;
}

interface PaymentCTAWithMembershipTermsProps {
  terms: string;
  marketingConsent?: string;
  marketingConsentValues: MarketingConsentInput;
  onSubmit: () => void;
  disabled: boolean;
  values: TermsInput;
  errors: FormikErrors<TermsInput>;
  touched: FormikTouched<TermsInput>;
  termsRequired: boolean;
  setFieldValue: (field: string, value: any) => void;
  onTermsClick: () => void;
  setFieldTouched?: (value: string) => void;
  onErrorTextRef?: (ref: HTMLElement | null, errorId: keyof TermsInput) => void;
  onInputBorderRef?: (
    ref: HTMLElement | null,
    errorId: keyof TermsInput
  ) => void;
  membershipPaymentCTALabel?: string;
}

export function PaymentCTAWithMembershipTerms(
  props: PaymentCTAWithMembershipTermsProps
) {
  const {
    values,
    errors,
    setFieldValue,
    onSubmit,
    disabled,
    terms,
    marketingConsent,
    marketingConsentValues,
    termsRequired,
    onTermsClick,
    touched,
    setFieldTouched,
    onErrorTextRef,
    onInputBorderRef,
    membershipPaymentCTALabel,
  } = props;
  const { fbt } = useI18n();
  const [open, setOpen] = useState(false);
  const [termsUrl, setTermsUrl] = useState<string>();

  /** This function to find the URL in the terms and conditions
   * To handle display this url in an iframe dialog
   */
  const contentClickHandler = useCallback(
    (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
      const targetLink = (e.target as Element).closest("a");
      if (!targetLink) return;
      onTermsClick();
      if (targetLink.target === "_blank") return;

      e.preventDefault();
      setTermsUrl(targetLink.href);
      setOpen(true);
    },
    [onTermsClick]
  );

  return (
    <div className="payment-cta-with-terms">
      <Spacer size={29} />
      <div className="row">
        <div className="col-12 mb-4">
          <div
            className="payment-cta-with-terms__terms payment-cta-with-terms__membership_terms text-break"
            onClick={contentClickHandler}
            dangerouslySetInnerHTML={{ __html: terms }}
          />
        </div>
        {termsRequired && (
          <div className="col-12 mb-4">
            <div className="payment-cta-with-terms__container">
              <Checkbox
                value={values.termsChecked}
                onChange={(v) => setFieldValue("termsChecked", v)}
                isInvalid={!!(errors.termsChecked && touched.termsChecked)}
                onBlur={() => setFieldTouched?.("termsChecked")}
                borderRef={(ref) => onInputBorderRef?.(ref, "termsChecked")}
              />

              <div className="payment-cta-with-terms__terms text-break">
                {fbt("I agree to the terms of service")}
              </div>
            </div>
            {errors.termsChecked && touched.termsChecked && (
              <Form.Text
                className="text-danger"
                ref={(ref: HTMLElement | null) =>
                  onErrorTextRef?.(ref, "termsChecked")
                }
              >
                {errors.termsChecked}
              </Form.Text>
            )}
          </div>
        )}
        {marketingConsent && (
          <div className="col-12 mb-4">
            <MarketingConsent
              marketingConsent={marketingConsent}
              values={marketingConsentValues}
              setFieldValue={setFieldValue}
            />
          </div>
        )}
        <div className="col-12">
          <Button
            variant="primary"
            size="lg"
            disabled={disabled}
            onClick={onSubmit}
            className="payment-cta__confirm"
          >
            <img
              src="https://meet-production.s3.us-west-1.amazonaws.com/icons/offerings/lock.svg"
              alt="payment confirm"
            />
            <h5 className="m-0">
              {fbt(membershipPaymentCTALabel || "Confirm and subscribe")}
            </h5>
          </Button>
        </div>
      </div>
      <Modal
        size="xl"
        fullscreen="sm-down"
        className="terms_and_conditions"
        show={open}
        onHide={() => setOpen(false)}
      >
        <Modal.Header closeButton></Modal.Header>
        <Modal.Body>
          <iframe
            className="offering-checkout-iframe"
            title="offering-checkout-iframe"
            src={termsUrl}
          />
        </Modal.Body>
      </Modal>
    </div>
  );
}
